import { ReactElement, Suspense, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { FilterValues } from "@kaltura/mediaspace-shared-types";
import { isSearchFilterValues, postKmsData } from "@kaltura/mediaspace-shared-utils";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { SearchResultsContainerProps } from "../search-results-container/SearchResultsContainer";
import { SearchResultsDisplayComponentProps } from "../search-results-container/types";

export interface SearchResultsContainerWithLoadMoreProps<
    SearchResultT extends AccumulatedResults<KalturaESearchEntryResult> = AccumulatedResults<KalturaESearchEntryResult>,
    PropsT = never,
> extends Omit<SearchResultsContainerProps<SearchResultT, PropsT>, "searchResultsDisplayComponent"> {
    /**
     * component that displays actual results with load more functionality
     */
    searchResultsDisplayComponent: (
        props: SearchResultsDisplayComponentProps<SearchResultT> &
            PropsT & {
                onLoadMore: () => void;
                isLoadingMore: boolean;
                updateCachedResults?: (updater: (oldData: SearchResultT) => SearchResultT) => void;
            }
    ) => ReactElement;

    /**
     * Optional callback to calculate next page parameters
     * If provided, will be used instead of default page-based pagination
     * @param accumulatedResults - current accumulated results
     * @param filterValues - current filter values
     * @param pageSize - page size
     * @returns parameters to send with the next request
     */
    getNextPageParams?: (
        accumulatedResults: SearchResultT,
        filterValues: FilterValues | undefined,
        pageSize: number
    ) => Record<string, unknown>;
}

export interface AccumulatedResults<T> {
    entryResults?: {
        objects: T[];
        totalCount: number;
    };
}

/**
 * Search Results Container with Load More functionality - accumulates results instead of replacing them
 */
export function SearchResultsContainerWithLoadMore<
    SearchResultT extends AccumulatedResults<KalturaESearchEntryResult> = AccumulatedResults<KalturaESearchEntryResult>,
    PropsT = never,
>(props: SearchResultsContainerWithLoadMoreProps<SearchResultT, PropsT>) {
    const {
        searchUrl,
        filterValues,
        pageSize,
        onEntryShowMore,
        isWideLayout,
        searchInLabel,
        searchResultsDisplayComponent: SearchResultsDisplayComponent,
        searchResultsSkeletonComponent: SearchResultsSkeletonComponent,
        bulkButtonActions,
        bulkMenuActions,
        displayComponentProps,
        areSearchResultsDraggable = false,
        getNextPageParams,
    } = props;

    const [page, setPage] = useState(1);
    const [invalidateCount, setInvalidateCount] = useState(0);
    // Hybrid approach: isLoadingMore tracks the async fetch operation, while useTransition
    // makes the subsequent state updates non-blocking. We can't use useTransition alone because
    // its isPending flag only tracks synchronous state updates inside startTransition(), not async operations.
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [renderTrigger, setRenderTrigger] = useState(0);
    const [isPending, startTransition] = useTransition();

    // Store accumulated results
    const accumulatedResultsRef = useRef<SearchResultT>({} as SearchResultT);

    // Only fetch page 1 with useFetchKmsData - this is for initial load with Suspense
    const requestParams = useMemo(() => ({ ...filterValues, page: 1, pageSize }), [filterValues, pageSize]);
    const fetchInitialData = useFetchKmsData<{ data: SearchResultT }>(
        { url: searchUrl, data: requestParams, abortable: true },
        // `invalidateCount` and `renderTrigger` are added to dependencies to reload the data when they change
        [searchUrl, requestParams, invalidateCount, renderTrigger]
    );

    // Fetch function that returns accumulated or initial data
    // useMemo ensures this function is recreated when renderTrigger changes,
    // forcing a fresh fetch after invalidation.
    const fetchAccumulatedData = useMemo(() => {
        return () => {
            // If we have accumulated data in the ref, always return it (including after cache updates)
            if (accumulatedResultsRef.current?.entryResults) {
                return { data: accumulatedResultsRef.current as SearchResultT };
            }
            // Otherwise fetch initial data (page 1) and populate the ref
            const { data } = fetchInitialData();
            const typedData = data as SearchResultT;
            accumulatedResultsRef.current = typedData;
            return { data: typedData };
        };
    }, [fetchInitialData, renderTrigger, invalidateCount]);

    // Function to update cached results - allows components to manipulate the cache
    const updateCachedResults = (updater: (oldData: SearchResultT) => SearchResultT) => {
        const updatedData = updater(accumulatedResultsRef.current);
        accumulatedResultsRef.current = updatedData;

        // Force re-render by triggering state update
        setRenderTrigger((prev) => prev + 1);
    };

    // Handle load more - use postKmsData to avoid Suspense
    const handleLoadMore = async () => {
        setIsLoadingMore(true);
        const nextPage = page + 1;

        try {
            // Use custom params if provided, otherwise use default page-based pagination
            const requestParams = getNextPageParams
                ? getNextPageParams(accumulatedResultsRef.current, filterValues, pageSize)
                : { ...filterValues, page: nextPage, pageSize };

            const result = await postKmsData(searchUrl, requestParams);
            const currentData = result.data as SearchResultT;

            if (currentData?.entryResults && accumulatedResultsRef.current?.entryResults) {
                // Capture values before transition to avoid TypeScript errors
                const currentObjects = accumulatedResultsRef.current.entryResults.objects;
                const newObjects = currentData.entryResults.objects;
                const totalCount = currentData.entryResults.totalCount;

                // Wrap state updates in startTransition to mark them as non-urgent.
                // This keeps the UI responsive during the update, allowing React to interrupt
                // the rendering if higher-priority updates (like user input) occur.
                startTransition(() => {
                    const updatedData = {
                        entryResults: {
                            objects: [...currentObjects, ...newObjects],
                            totalCount,
                        },
                    } as SearchResultT;
                    accumulatedResultsRef.current = updatedData;
                    setPage(nextPage);
                    setIsLoadingMore(false);
                });
            }
            else {
                setIsLoadingMore(false);
            }
        }
        catch (error) {
            console.error("Error loading more results:", error);
            setIsLoadingMore(false);
        }
    };

    // reset page on param change
    useEffect(() => {
        setPage(1);
        accumulatedResultsRef.current = {} as SearchResultT;
        setIsLoadingMore(false);
    }, [filterValues]);

    /**
     * Clear the ref when invalidateCount changes (full invalidation like Delete All).
     *
     * This useEffect runs AFTER the component re-renders and AFTER useFetchKmsData's useEffect has run.
     * This timing is crucial because:
     * 1. onInvalidate increments invalidateCount
     * 2. Component re-renders with old fetchInitialData (useFetchKmsData hasn't updated yet)
     * 3. useFetchKmsData's useEffect runs and creates new fetchInitialData
     * 4. This useEffect runs and clears the ref
     * 5. Next render uses the new fetchInitialData with empty ref, fetching fresh data
     *
     * Note: We don't clear on renderTrigger changes because that's used for cache updates (single item removal)
     */
    useEffect(() => {
        accumulatedResultsRef.current = {} as SearchResultT;
    }, [invalidateCount]);

    const onInvalidate = () => {
        // Trigger full invalidation by incrementing invalidateCount
        // The ref will be cleared by the useEffect above after the render cycle
        setPage(1);
        setIsLoadingMore(false);
        setInvalidateCount((prev) => prev + 1);
        setRenderTrigger((prev) => prev + 1);
    };

    // render with Suspense for initial load only
    const componentProps: SearchResultsDisplayComponentProps<SearchResultT> &
        PropsT & {
            onLoadMore: () => void;
            isLoadingMore: boolean;
            updateCachedResults?: (updater: (oldData: SearchResultT) => SearchResultT) => void;
        } = {
        isSearch: isSearchFilterValues(filterValues),
        isTextSearch: !!filterValues?.keyword,
        fetchModuleData: fetchAccumulatedData,
        page,
        onPageChange: handleLoadMore,
        pageSize,
        onEntryShowMore,
        isWideLayout,
        searchInLabel,
        bulkButtonActions,
        bulkMenuActions,
        onInvalidate,
        filterValues,
        areSearchResultsDraggable,
        onLoadMore: handleLoadMore,
        isLoadingMore,
        updateCachedResults,
        ...displayComponentProps,
    } as SearchResultsDisplayComponentProps<SearchResultT> &
        PropsT & {
            onLoadMore: () => void;
            isLoadingMore: boolean;
            updateCachedResults?: (updater: (oldData: SearchResultT) => SearchResultT) => void;
        };

    return (
        <ErrorBoundary fallback={<SearchResultsSkeletonComponent />}>
            <Suspense fallback={<SearchResultsSkeletonComponent />}>
                <SearchResultsDisplayComponent {...componentProps} />
            </Suspense>
        </ErrorBoundary>
    );
}

export default SearchResultsContainerWithLoadMore;
